import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { PermissionService } from '../security/permission.service';
import { app_permissions } from '../security/app-permissions';
import { SessionStorageService } from '../services/session-storage.service';
import { BusinessEventsService } from './business-events.service';
import {
  BusinessEventActionChannel,
  BusinessEventActionExecution,
  BusinessEventActionExecutionStatus,
  BusinessEventDefinition,
  PageResponse
} from './business-events.model';
import { prettyJson } from './business-events-json.util';

@Component({
  selector: 'app-business-event-executions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    @if (isLoading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="Administration" title="Business Event Executions" subtitle="Review queued, completed, and failed action executions.">
        <div nav>
          <a pButton routerLink="/business-events/alerts" label="Alerts" icon="pi pi-bolt" [outlined]="true" severity="secondary"></a>
          <a pButton routerLink="/business-events/teams" label="Teams" icon="pi pi-users" [outlined]="true" severity="secondary"></a>
          <a pButton routerLink="/business-events/templates" label="Templates" icon="pi pi-file-edit" [outlined]="true" severity="secondary"></a>
          <a pButton routerLink="/business-events/executions" label="Executions" icon="pi pi-history"></a>
        </div>
        <div actions>
          <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary" (onClick)="loadExecutions()"></p-button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-page-search-panel
        title="Execution Search"
        [activeFilters]="activeFilterCount"
        [resultText]="page.totalElements + ' total executions'"
        [collapsed]="false"
        (search)="search()"
        (clear)="clearFilters()">
        <div class="be-filter-grid">
          <label>
            <span>Event</span>
            <p-select [options]="eventOptions" [(ngModel)]="filters.eventCode" optionLabel="label" optionValue="value" placeholder="All events" [showClear]="true"></p-select>
          </label>
          <label>
            <span>Status</span>
            <p-select [options]="statusOptions" [(ngModel)]="filters.status" optionLabel="label" optionValue="value" placeholder="All statuses" [showClear]="true"></p-select>
          </label>
          <label>
            <span>Channel</span>
            <p-select [options]="channelOptions" [(ngModel)]="filters.channel" optionLabel="label" optionValue="value" placeholder="All channels" [showClear]="true"></p-select>
          </label>
        </div>
      </app-page-search-panel>

      <app-page-data-panel title="Executions" subtitle="Backend-paged action execution history." [resultText]="displayedExecutions.length + ' visible'">
        <p-table [value]="displayedExecutions" [loading]="isLoading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '980px' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Execution</th>
              <th>Channel</th>
              <th>Status</th>
              <th>Attempts</th>
              <th>Timing</th>
              <th>Failure</th>
              <th class="text-right">Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-execution>
            <tr>
              <td>
                <div class="be-stack">
                  <strong>#{{ execution.id }}</strong>
                  <small>Event log {{ execution.eventLogId }} · Rule {{ execution.ruleId }} · Action {{ execution.actionId }}</small>
                </div>
              </td>
              <td><p-tag [value]="execution.channel" [severity]="channelSeverity(execution.channel)"></p-tag></td>
              <td><p-tag [value]="execution.status" [severity]="statusSeverity(execution.status)"></p-tag></td>
              <td>{{ execution.attemptCount || 0 }} / {{ execution.maxAttempts || 0 }}</td>
              <td>
                <div class="be-stack">
                  <span>{{ execution.createdAt | date:'short' }}</span>
                  <small>Next {{ execution.nextAttemptAt ? (execution.nextAttemptAt | date:'short') : '-' }}</small>
                </div>
              </td>
              <td>
                <span class="be-failure">{{ execution.failureReason || '-' }}</span>
              </td>
              <td class="text-right">
                <div class="be-row-actions">
                  <p-button icon="pi pi-eye" [text]="true" severity="secondary" pTooltip="View details" (onClick)="openDetails(execution)"></p-button>
                  <p-button icon="pi pi-refresh" [text]="true" severity="warn" pTooltip="Retry execution" (onClick)="retry(execution)" [disabled]="!canRetry || execution.status !== 'FAILED'"></p-button>
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="7" class="text-center p-4">No executions match the current filters.</td></tr>
          </ng-template>
        </p-table>

        <div class="be-pager">
          <span>Page {{ page.number + 1 }} of {{ page.totalPages || 1 }}</span>
          <div>
            <p-button label="Previous" icon="pi pi-chevron-left" [outlined]="true" severity="secondary" (onClick)="goToPage(page.number - 1)" [disabled]="page.first || isLoading"></p-button>
            <p-button label="Next" icon="pi pi-chevron-right" iconPos="right" [outlined]="true" severity="secondary" (onClick)="goToPage(page.number + 1)" [disabled]="page.last || isLoading"></p-button>
          </div>
        </div>
      </app-page-data-panel>
    </app-page-shell>

    <p-dialog
      header="Execution Details"
      [(visible)]="detailsVisible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: 'min(980px, 96vw)' }">
      @if (selectedExecution) {
        <div class="be-detail-grid">
          <section>
            <h3>Message</h3>
            <label>Subject</label>
            <pre>{{ selectedExecution.renderedSubject || '-' }}</pre>
            <label>Body</label>
            <pre>{{ selectedExecution.renderedBody || '-' }}</pre>
          </section>
          <section>
            <h3>Recipients</h3>
            <pre>{{ prettyJsonText(selectedExecution.resolvedRecipientsJson) || '-' }}</pre>
            <h3>Attachments</h3>
            <pre>{{ prettyJsonText(selectedExecution.resolvedAttachmentsJson) || '-' }}</pre>
          </section>
          <section>
            <h3>Provider</h3>
            <label>Reference</label>
            <pre>{{ selectedExecution.providerReference || '-' }}</pre>
            <label>Failure</label>
            <pre>{{ selectedExecution.failureReason || '-' }}</pre>
          </section>
        </div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Close" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="detailsVisible = false"></p-button>
        <p-button label="Retry" icon="pi pi-refresh" severity="warn" (onClick)="selectedExecution && retry(selectedExecution)" [disabled]="!selectedExecution || !canRetry || selectedExecution.status !== 'FAILED'"></p-button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .be-filter-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(180px, 1fr));
      gap: .85rem;
    }

    label {
      display: grid;
      gap: .35rem;
      min-width: 0;
      color: #334155;
    }

    label span,
    .be-detail-grid label {
      color: #64748b;
      font-size: .82rem;
      font-weight: 700;
    }

    p-select {
      width: 100%;
    }

    .be-stack {
      display: flex;
      flex-direction: column;
      gap: .25rem;
      min-width: 0;
    }

    .be-stack small {
      color: #64748b;
      font-size: .78rem;
    }

    .be-failure {
      display: inline-block;
      max-width: 280px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .be-row-actions {
      display: flex;
      justify-content: flex-end;
      gap: .25rem;
    }

    .be-pager {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: .75rem;
      margin-top: .85rem;
    }

    .be-pager > div {
      display: flex;
      gap: .5rem;
    }

    .be-detail-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 1rem;
    }

    .be-detail-grid section {
      min-width: 0;
      border: 1px solid #dbe7f3;
      border-radius: 8px;
      background: #f8fafc;
      padding: .85rem;
    }

    .be-detail-grid h3 {
      color: #172033;
      font-size: 1rem;
      margin: 0 0 .6rem;
    }

    .be-detail-grid pre {
      color: #334155;
      white-space: pre-wrap;
      word-break: break-word;
      margin: .35rem 0 .85rem;
    }

    @media (max-width: 960px) {
      .be-filter-grid,
      .be-detail-grid {
        grid-template-columns: 1fr;
      }

      .be-pager {
        align-items: stretch;
        flex-direction: column;
      }
    }
  `]
})
export class BusinessEventExecutionsComponent implements OnInit {
  private readonly businessEventsService = inject(BusinessEventsService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);
  private readonly sessionStorageService = inject(SessionStorageService);

  definitions: BusinessEventDefinition[] = [];
  page: PageResponse<BusinessEventActionExecution> = {
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 25,
    number: 0,
    first: true,
    last: true
  };
  filters: { eventCode: string; status: BusinessEventActionExecutionStatus | ''; channel: BusinessEventActionChannel | '' } = {
    eventCode: '',
    status: '',
    channel: ''
  };
  metrics: PageMetric[] = [];
  isLoading = false;
  canRetry = false;
  detailsVisible = false;
  selectedExecution?: BusinessEventActionExecution;

  readonly channelOptions = [
    { label: 'WhatsApp', value: 'WHATSAPP' as BusinessEventActionChannel },
    { label: 'Email', value: 'EMAIL' as BusinessEventActionChannel },
    { label: 'SMS', value: 'SMS' as BusinessEventActionChannel },
    { label: 'In-app productivity', value: 'PRODUCTIVITY' as BusinessEventActionChannel },
    { label: 'Mobile push', value: 'FCM' as BusinessEventActionChannel },
    { label: 'Webhook', value: 'WEBHOOK' as BusinessEventActionChannel }
  ];

  readonly statusOptions = [
    { label: 'Queued', value: 'QUEUED' as BusinessEventActionExecutionStatus },
    { label: 'Running', value: 'RUNNING' as BusinessEventActionExecutionStatus },
    { label: 'Succeeded', value: 'SUCCEEDED' as BusinessEventActionExecutionStatus },
    { label: 'Failed', value: 'FAILED' as BusinessEventActionExecutionStatus },
    { label: 'Skipped', value: 'SKIPPED' as BusinessEventActionExecutionStatus }
  ];

  ngOnInit(): void {
    this.canRetry = this.permissionService.hasPermission(app_permissions.business_events.retry);
    this.loadDefinitions();
    this.loadExecutions();
  }

  get eventOptions(): { label: string; value: string }[] {
    return this.definitions.map(definition => ({
      label: `${definition.moduleCode || 'Operations'} - ${definition.displayName}`,
      value: definition.eventCode
    }));
  }

  get activeFilterCount(): number {
    return [this.filters.eventCode, this.filters.status, this.filters.channel].filter(value => !!value).length;
  }

  get displayedExecutions(): BusinessEventActionExecution[] {
    if (!this.filters.channel) {
      return this.page.content;
    }
    return this.page.content.filter(execution => execution.channel === this.filters.channel);
  }

  loadDefinitions(): void {
    this.businessEventsService.getDefinitions().subscribe({
      next: definitions => this.definitions = definitions ?? [],
      error: () => this.definitions = []
    });
  }

  loadExecutions(pageNumber = this.page.number): void {
    this.isLoading = true;
    this.businessEventsService.getExecutions({
      companyId: this.companyId(),
      eventCode: this.filters.eventCode,
      status: this.filters.status,
      page: pageNumber,
      size: this.page.size || 25
    }).subscribe({
      next: page => {
        this.page = {
          ...page,
          content: page.content ?? [],
          totalElements: page.totalElements ?? 0,
          totalPages: page.totalPages ?? 0,
          number: page.number ?? pageNumber,
          size: page.size ?? 25
        };
        this.updateMetrics();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Executions', detail: 'Unable to load business event executions.' });
      }
    });
  }

  search(): void {
    this.loadExecutions(0);
  }

  clearFilters(): void {
    this.filters = { eventCode: '', status: '', channel: '' };
    this.loadExecutions(0);
  }

  goToPage(pageNumber: number): void {
    if (pageNumber < 0 || (this.page.totalPages && pageNumber >= this.page.totalPages)) return;
    this.loadExecutions(pageNumber);
  }

  openDetails(execution: BusinessEventActionExecution): void {
    this.selectedExecution = execution;
    this.detailsVisible = true;
  }

  retry(execution: BusinessEventActionExecution): void {
    if (!this.canRetry || execution.status !== 'FAILED') return;
    this.businessEventsService.retryExecution(execution.id).subscribe({
      next: retried => {
        this.messageService.add({ severity: 'success', summary: 'Retry Queued' });
        if (this.selectedExecution?.id === retried.id) {
          this.selectedExecution = retried;
        }
        this.loadExecutions();
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Retry Failed', detail: error?.error?.message ?? 'Unable to retry the execution.' })
    });
  }

  prettyJsonText(value?: string | null): string {
    return prettyJson(value);
  }

  statusSeverity(status: BusinessEventActionExecutionStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'SUCCEEDED') return 'success';
    if (status === 'FAILED') return 'danger';
    if (status === 'RUNNING') return 'info';
    if (status === 'QUEUED') return 'warn';
    return 'secondary';
  }

  channelSeverity(channel: BusinessEventActionChannel): 'success' | 'info' | 'warn' {
    if (channel === 'WHATSAPP') return 'success';
    if (channel === 'EMAIL') return 'info';
    return 'warn';
  }

  private updateMetrics(): void {
    const content = this.page.content ?? [];
    this.metrics = [
      { label: 'Total', value: this.page.totalElements, tone: 'info' },
      { label: 'Queued', value: content.filter(execution => execution.status === 'QUEUED').length, tone: 'warn' },
      { label: 'Succeeded', value: content.filter(execution => execution.status === 'SUCCEEDED').length, tone: 'success' },
      { label: 'Failed', value: content.filter(execution => execution.status === 'FAILED').length, tone: 'danger' }
    ];
  }

  private companyId(): number {
    return this.sessionStorageService.getCompany()?.id ?? this.businessEventsService.getCompanyId();
  }
}
